import DesignTokens from '@tableau/design-tokens';
import { ArrowChevronLeftIcon } from '@tableau/icons/ArrowChevronLeftIcon';
import { ArrowChevronRightIcon } from '@tableau/icons/ArrowChevronRightIcon';
import { ButtonType, ButtonWidget, InteractionState } from '@tableau/widgets';
import React from 'react';

import { Telemetry } from '~commonInfra/services/Telemetry';
import { LeftNavCollapseExpandClickEvent, TelemetryEvents } from '~commonInfra/telemetryEvents/TelemetryEvents';
import { selectLayoutType } from '~src/spacesPlatform/selectors/LayoutTypeSelector';
import { applyColor } from '~src/style/ColorService';

import { getAppStore } from '../../../bootstrap/redux/store/AppStore';
import { Messages } from '../../../codegen/Localize';
import { LeftNavVisibility } from './LeftNav';
import { NavStyles } from './NavigationStyles';

// When we have the mobile navigation dropdown, PanelState will include those states (open/close) as well
export type PanelState = LeftNavVisibility;

export interface NavPanelButtonProps {
  readonly panelState: PanelState;
  readonly visibleVerticalScroll: boolean;
  readonly panelOpenMessage: string;
  readonly toggleNavPanel: () => void;
}

/** The button to open/close/minimize the navigation panel, whether it's in a dropdown or in the sidebar/leftnav */
export class NavPanelButton extends React.Component<NavPanelButtonProps> {
  public render(): JSX.Element {
    return (
      <div className={NavStyles.LeftPanelButton}>
        <ButtonWidget
          buttonType={ButtonType.LowEmphasis}
          getContent={this.getButtonContent}
          title={this.isPanelOpen() ? Messages.leftNavToggleButtonCollapseTitle() : Messages.leftNavToggleButtonExpandTitle()}
          aria-label={this.isPanelOpen() ? Messages.leftNavToggleButtonCollapseAriaLabel() : Messages.leftNavToggleButtonExpandAriaLabel()}
          testId={this.isPanelOpen() ? 'close-left-nav' : 'open-left-nav'}
          handleClick={this.onClick}
          style={NavStyles.ButtonStyle}
        />
      </div>
    );
  }

  private onClick = () => {
    Telemetry.sendEvent<LeftNavCollapseExpandClickEvent>({
      type: TelemetryEvents.LeftNavCollapseExpandClick,
      action: this.isPanelOpen() ? 'collapse' : 'expand',
      layoutType: selectLayoutType(getAppStore().getState())
    });
    this.props.toggleNavPanel();
  };

  private getButtonContent = (interactiveState: InteractionState): JSX.Element => {
    const IconComponent = this.isPanelOpen() ? ArrowChevronLeftIcon : ArrowChevronRightIcon;
    const iconAlt = this.isPanelOpen() ? Messages.leftNavToggleButtonCollapseTitle() : Messages.leftNavToggleButtonExpandTitle();
    return (
      <div className={NavStyles.getButtonContentClassNames(this.props.visibleVerticalScroll)}>
        <IconComponent
          // className governs the 24px box; size={22} picks the nearest glyph variant (24 is not a valid size variant)
          className={NavStyles.ButtonContentIcon}
          size={22}
          color={(interactiveState.hover || interactiveState.active)
            ? applyColor(DesignTokens.color.font.default.default, '#333')
            : applyColor(DesignTokens.color.font.default.secondary, '#666')}
          alt={iconAlt}
        />
      </div>
    );
  };

  private isPanelOpen() {
    return this.props.panelState === 'expanded';
  }
}
